import axios from "axios";

const TOKEN_KEY = "user_token";
const USER_KEY = "user_user";
const PENDING_EMAIL_KEY = "user_pending_email";

const basePath = process.env.REACT_APP_BASE_PATH || "";

export const userAuth = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  getUser: () => {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY)) || null;
    } catch {
      return null;
    }
  },
  setSession: (token, user) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user || {}));
  },
  setUser: (user) => localStorage.setItem(USER_KEY, JSON.stringify(user || {})),
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
  isLoggedIn: () => !!localStorage.getItem(TOKEN_KEY),

  // Email waiting for OTP verification — survives a refresh of the OTP page.
  // `from` tells the OTP screen where the "change email" pencil goes back to.
  getPendingEmail: () => sessionStorage.getItem(PENDING_EMAIL_KEY) || "",
  setPendingEmail: (email) => sessionStorage.setItem(PENDING_EMAIL_KEY, email),
  clearPendingEmail: () => sessionStorage.removeItem(PENDING_EMAIL_KEY),
};

// User (client) endpoints live at the API root, guarded by auth:sanctum.
const userApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: { Accept: "application/json" },
});

userApi.interceptors.request.use((config) => {
  const token = userAuth.getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

userApi.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error?.response?.status === 401 && userAuth.getToken()) {
      userAuth.clear();
      window.location.assign(`${basePath}/user/login`);
    }
    return Promise.reject(error);
  }
);

const toFormData = (payload) => {
  const fd = new FormData();
  Object.entries(payload).forEach(([k, v]) => {
    if (v !== undefined && v !== null) fd.append(k, v);
  });
  return fd;
};

const webDevice = { deviceType: "web", deviceToken: "", deviceVersion: "", appVersion: "" };

export const userService = {
  register: ({ name, email, phone }) =>
    userApi.post("/register", toFormData({ name, email, phone, language: "en", ...webDevice })),
  // is_company=0 → only individual accounts; business accounts are rejected by the API.
  login: (email) => userApi.post("/login", toFormData({ email, is_company: 0, ...webDevice })),
  verifyOTP: (email, otp) => userApi.post("/verifyOTP", toFormData({ email, otp, ...webDevice })),
  resendOTP: (email) => userApi.post("/resendOTP", toFormData({ email })),
  guestLogin: () => userApi.post("/guestLogin", toFormData({ language: "en", ...webDevice })),
  googleSignin: ({ name, email, googleId, idToken }) =>
    userApi.post("/google-signin", toFormData({ name, email, googleId, idToken, ...webDevice })),
  logout: () => userApi.post("/logout"),

  getProfile: () => userApi.get("/getProfile"),
  // fields: any of name, email, phone, picture (File), app_lang, notification_enable
  updateProfile: (fields) => userApi.post("/update-profile", toFormData(fields)),
  deleteAccount: () => userApi.post("/delete-account"),
  getDashboard: (page = 1) => userApi.get("/getDashboard", { params: { page } }),
  markBannerViewed: (bannerId) => userApi.post("/markBannerViewed", toFormData({ banner_id: bannerId })),

  // Lawyers. companyType: "" | "Company" | "Individual".
  // filters: { categories: [id], jurisdictions: [id], online: bool }
  getLawyers: (page = 1, search = "", companyType = "", filters = {}) =>
    userApi.post(
      "/filterLawyers",
      toFormData({
        page,
        search: search || undefined,
        company_type: companyType || undefined,
        categories: filters.categories?.length ? JSON.stringify(filters.categories.map(Number)) : undefined,
        jurisdictions: filters.jurisdictions?.length ? JSON.stringify(filters.jurisdictions.map(Number)) : undefined,
        online: filters.online ? 1 : undefined,
      })
    ),
  getLawyerDetails: (lawyerId) => userApi.get("/getLawyerDetails", { params: { lawyer_id: lawyerId } }),
  checkLawyerAvailability: (lawyerId) =>
    userApi.post("/checkLawyerAvailability", toFormData({ lawyer_id: lawyerId })),
  // Purchased lawyer services ("My Lawyers"). status: "active" | "inactive".
  getMyServices: (status, page = 1) => userApi.get("/getMyServices", { params: { status, page } }),

  // Payments. Each returns { paymentIntent (client secret), publishableKey }.
  servicePayment: (lawyerId, amount) => userApi.post("/stripePayment", toFormData({ lawyer_id: lawyerId, amount })),
  // period: "weekly" (one-time service) | "monthly"
  buyService: ({ lawyerId, period, transactionId }) =>
    userApi.post(
      "/buyService",
      toFormData({ lawyer_id: lawyerId, period, transaction_id: transactionId, payment_method: "card" })
    ),
  invoicePayment: (amount) => userApi.post("/paymentForInvoice", toFormData({ amount })),
  invoicePaid: (messageId, transactionId) =>
    userApi.post("/invoicePaid", toFormData({ message_id: messageId, transaction_id: transactionId })),
  questionPayment: (amount) => userApi.post("/paymentForQuestion", toFormData({ amount })),

  // Video calls. Consultation = broadcast to matched lawyers; direct = ring one lawyer.
  getCallPackages: () => userApi.get("/getCallPackages"),
  // Authorize-only (manual capture); nothing is charged until the call ends.
  consultationPayment: (amount, packageId) =>
    userApi.post("/consultationPayment", toFormData({ amount, call_package_id: packageId })),
  storeCallHistory: (fields) => userApi.post("/storeCallHistory", toFormData(fields)),
  initiateConsultation: (callId) => userApi.post("/initiateConsultation", toFormData({ call_id: callId })),
  initiateCall: (fields) => userApi.post("/initiateCall", toFormData(fields)),
  cancelCall: (callId) => userApi.post("/cancelCall", toFormData({ callId })),
  endCall: (callId) => userApi.post("/endCall", toFormData({ callId })),
  // Attach a paid package (already authorized via consultationPayment) to a live free call.
  extendCall: (callId, packageId, transactionId) =>
    userApi.post("/extendCall", toFormData({ callId, call_package_id: packageId, transaction_id: transactionId })),
  getCallHistory: (page = 1) => userApi.get("/getCallHistory", { params: { page } }),

  // Chats
  getChats: (page = 1, type = "normal") => userApi.get("/getChats", { params: { page, type } }),
  getChatMessages: (chatId, page = 1) => userApi.get("/getChatMessages", { params: { chat_id: chatId, page } }),
  // Opens (creating if needed) the chat for a purchased service or with a lawyer; resolves data.chat.
  openServiceChat: (userServiceId) => userApi.get("/getChatMessages", { params: { user_service_id: userServiceId } }),
  openLawyerChat: (lawyerId) => userApi.get("/getChatMessages", { params: { lawyer_id: lawyerId } }),
  // payload: { chat_id, message?, file?, file_type?, reply_to_id? } — multipart for files.
  sendMessage: (payload) => userApi.post("/sendMessage", toFormData(payload)),
  readChatMessages: (chatId) => userApi.post("/readChatMessages", toFormData({ chat_id: chatId })),

  // "Legal Platform Support" chat with the admin team (one per account).
  getSupportChat: () => userApi.get("/getSupportChat"),
  getSupportChatMessages: (page = 1) => userApi.get("/getSupportChatMessages", { params: { page } }),
  // payload: { message?, file?, file_type? } — multipart for files.
  sendSupportMessage: (payload) => userApi.post("/sendSupportMessage", toFormData(payload)),
  readSupportMessages: () => userApi.post("/readSupportMessages"),
  // Voice/video calls with the support team (SupportCallManager). type: "voice" | "video".
  acceptSupportCall: (callId) => userApi.post("/acceptSupportCall", { callId }),
  // reason: "missed" (cancelled / unanswered) or "rejected" (declined); ignored once answered.
  endSupportCall: (callId, reason) => userApi.post("/endSupportCall", { callId, reason }),

  // Questions ("My Request")
  getMyQuestions: (page = 1) => userApi.get("/getMyQuestions", { params: { page } }),
  getQuestionDetails: (questionId) => userApi.get("/myQuestionDetails", { params: { question_id: questionId } }),
  closeQuestion: (questionId) => userApi.post("/closeQuestion", toFormData({ question_id: questionId })),
  addQuestion: ({ question, jurisdictionId, attachments = [] }) => {
    const fd = toFormData({ question, jurisdiction_id: jurisdictionId, doc_formats: "docs", max_documents: 2 });
    attachments.forEach((f) => fd.append("attachments[]", f));
    return userApi.post("/addQuestion", fd);
  },

  getNotifications: () => userApi.get("/getNotifications"),
  markNotificationRead: (id) => userApi.post("/markasReadNotification", toFormData({ notification_id: id })),
  clearNotifications: () => userApi.post("/clearAllNotifications"),

  // Public lookups
  getCategories: () => axios.get(`${process.env.REACT_APP_API_URL}/getCategories`, { headers: { Accept: "application/json" } }),
  // getFor "call" → only jurisdictions some active lawyer covers (video call flow).
  getDropdownData: (getFor) =>
    axios.get(`${process.env.REACT_APP_API_URL}/getDropdownData`, {
      headers: { Accept: "application/json" },
      params: getFor ? { get_for: getFor } : undefined,
    }),
  draftCaseWithAI: (text, categoryId, jurisdictionId) =>
    userApi.post("/assistDescription", {
      text: (text || "").slice(0, 1000),
      category_id: categoryId || undefined,
      jurisdiction_id: jurisdictionId || undefined,
    }),
};

// Files the API has no upload for resolve to ".../blank.png" rather than null.
export const isBlankFile = (url) => !url || /\/blank\.png$/i.test(url);

// Pulls the most useful error message out of an axios error / API response.
export const apiErrorMessage = (err, fallback = "Something went wrong. Please try again.") =>
  err?.response?.data?.message ||
  (err?.response?.data?.errors && Object.values(err.response.data.errors)[0]?.[0]) ||
  fallback;

export default userApi;
